import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { SuccessFetchListResponse } from "@/services/api";
import { QueryKey, UseQueryResult } from "@tanstack/react-query";
import { forwardRef, ReactNode } from "react";
import { ControllerRenderProps } from "react-hook-form";
import CommandTree from "./components/CommandTree";
import TriggerButton from "./components/TriggerButton";
import { useAsyncSelect } from "./hooks/useAsyncSelect";

export interface Option {
  [key: string]: unknown;
  id: string;
  name?: string;
}

export type AsyncSelectFetchOn = "open" | "mount";

export interface AsyncSelectProps<T extends Option>
  extends ControllerRenderProps {
  loadingLabel?: string;
  searchLabel?: string;
  noResultsLabel?: string;
  placeholder?: string;
  queryKey: QueryKey;
  queryFn: () => Promise<SuccessFetchListResponse<T>>;
  /** Controls whether data fetching is allowed for the current state. */
  enabled?: boolean;
  /** `"mount"` (default): fetch on mount. `"open"`: fetch when the dropdown opens. */
  fetchOn?: AsyncSelectFetchOn;
  enableFilter?: boolean;
  className?: string;
  onFilter?: (value: string) => void;
  inDialog?: boolean;
  clear?: boolean;
  allowClearSelection?: boolean;
  clearLabel?: string;
  renderName?: (item: T) => React.ReactNode;
  defaultItem?: Partial<T>;
  footer?: (
    query: UseQueryResult<SuccessFetchListResponse<Option>, Error>,
  ) => ReactNode;
}

/** Base props for thin wrappers around `AsyncSelect`. */
export interface AsyncSelectBaseProps extends ControllerRenderProps {
  enabled?: boolean;
  fetchOn?: AsyncSelectFetchOn;
}

interface WithForwardRefType
  extends React.ForwardRefExoticComponent<
    Omit<AsyncSelectProps<Option>, "ref"> &
      React.RefAttributes<HTMLButtonElement>
  > {
  <T extends Option>(
    props: AsyncSelectProps<T> & React.RefAttributes<HTMLButtonElement>,
  ): ReturnType<
    React.FC<AsyncSelectProps<T> & React.RefAttributes<HTMLButtonElement>>
  >;
}

const AsyncSelect: WithForwardRefType = forwardRef<
  HTMLButtonElement,
  AsyncSelectProps<Option>
>(
  (
    {
      onFilter,
      onChange,
      value,
      loadingLabel,
      searchLabel,
      noResultsLabel,
      placeholder,
      queryKey,
      queryFn,
      enabled = true,
      fetchOn = "mount",
      enableFilter = true,
      className,
      inDialog,
      clear,
      allowClearSelection = false,
      clearLabel,
      renderName,
      defaultItem: defaultItemProp,
      footer,
      disabled,
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile();

    const {
      open,
      onOpenChange,
      query,
      data,
      isFetching,
      filterInputValue,
      onValueChange,
      onClear,
      onSelect,
      onClearSelection,
      selectedId,
      buttonLabel,
    } = useAsyncSelect({
      value,
      onChange,
      queryKey,
      queryFn,
      enabled,
      fetchOn,
      onFilter,
      defaultItem: defaultItemProp,
      renderName,
    });

    const triggerButton = (
      <TriggerButton
        buttonLabel={buttonLabel}
        placeholder={placeholder}
        disabled={disabled}
      />
    );

    const commandTree = (
      <CommandTree
        enableFilter={enableFilter}
        filterInputValue={filterInputValue}
        onValueChange={onValueChange}
        onClear={onClear}
        searchLabel={searchLabel}
        isFetching={isFetching}
        loadingLabel={loadingLabel}
        noResultsLabel={noResultsLabel}
        data={data}
        selectedId={selectedId}
        renderName={renderName}
        onSelect={onSelect}
        allowClearSelection={allowClearSelection}
        clearLabel={clearLabel}
        onClearSelection={onClearSelection}
        inputProps={props}
      />
    );

    return (
      <>
        {isMobile ? (
          <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetTrigger asChild {...props} ref={ref} className={className}>
              {triggerButton}
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="flex h-dvh flex-col gap-0 p-0 [&>button.absolute]:w-6 [&>button.absolute]:h-6"
            >
              <SheetTitle className="sr-only">
                {placeholder ?? searchLabel ?? ""}
              </SheetTitle>
              <div className="flex items-center justify-between border-b px-3 py-5">
                <span className="truncate text-base font-medium">
                  {placeholder}
                </span>
              </div>
              {commandTree}
            </SheetContent>
          </Sheet>
        ) : (
          <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild {...props} ref={ref} className={className}>
              {triggerButton}
            </PopoverTrigger>
            <PopoverContent
              className={cn("w-[--radix-popover-trigger-width] p-0", {
                "pointer-events-auto": inDialog,
              })}
              onWheel={inDialog ? (e) => e.stopPropagation() : undefined}
              onTouchMove={inDialog ? (e) => e.stopPropagation() : undefined}
            >
              {commandTree}
            </PopoverContent>
          </Popover>
        )}
        {footer?.(query)}
      </>
    );
  },
);

export default AsyncSelect;
